import {
  AuditLogResource,
  ListAuditLogsFilterParameterResourceTypeEq,
  PayeeRequirement,
  RequirementDataSourceType
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";

/**
 * A requirement's data source and the audit log's resource types are two
 * separate v3 enums that only mostly overlap, so the timeline names each data
 * source explicitly rather than passing its type through.
 *
 * `BiometricIdentityVerification` has no audit-log resource type — it is a
 * distinct resource from `IdentityVerificationRequest`, which belongs to the
 * non-biometric identity-verification flow — so those data sources contribute no
 * events either way. They are dropped rather than sent: the timeline fans out
 * with `Promise.all`, so if the API rejects the unknown filter value it takes
 * the requirement's whole history down with it.
 *
 * Typing this as a total `Record` means a new data-source type fails to compile
 * here rather than silently going missing from the timeline.
 */
const DATA_SOURCE_RESOURCE_TYPES: Record<
  RequirementDataSourceType,
  ListAuditLogsFilterParameterResourceTypeEq | null
> = {
  [RequirementDataSourceType.SHARED_FILE_REQUEST]:
    ListAuditLogsFilterParameterResourceTypeEq.SHARED_FILE_REQUEST,
  [RequirementDataSourceType.BACKGROUND_CHECK]:
    ListAuditLogsFilterParameterResourceTypeEq.BACKGROUND_CHECK,
  [RequirementDataSourceType.INSURANCE_MONITOR]:
    ListAuditLogsFilterParameterResourceTypeEq.INSURANCE_MONITOR,
  [RequirementDataSourceType.SIGNATURE_REQUEST]:
    ListAuditLogsFilterParameterResourceTypeEq.SIGNATURE_REQUEST,
  [RequirementDataSourceType.BIOMETRIC_IDENTITY_VERIFICATION]: null
};

export function useRequirementHistoryResources(
  requirement: PayeeRequirement
): AuditLogResource[] {
  return useMemo(() => {
    const resources: AuditLogResource[] = [];

    if (requirement.id) {
      resources.push({
        resourceType: ListAuditLogsFilterParameterResourceTypeEq.REQUIREMENT,
        resourceId: requirement.id
      });
    }

    const { dataSource } = requirement;
    const dataSourceResourceType = dataSource
      ? DATA_SOURCE_RESOURCE_TYPES[dataSource.type]
      : null;

    if (dataSource && dataSourceResourceType) {
      resources.push({
        resourceType: dataSourceResourceType,
        resourceId: dataSource.id
      });

      requirement.dataSourceHistory?.forEach(history => {
        if (history.dataSourceId) {
          resources.push({
            resourceType: dataSourceResourceType,
            resourceId: history.dataSourceId
          });
        }
      });
    }

    return resources;
  }, [requirement]);
}
